import React, { useEffect, useState, useRef } from 'react';
import { Button, Card, Col, Collapse, message, Pagination, Row, Typography, Empty, Spin, Input, Modal, Select } from 'antd';
import axios from '../util/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../context/PaginationProvider';
import { EditOutlined, SendOutlined, EyeOutlined, MinusOutlined, PlusOutlined, CheckOutlined, CloseOutlined, FileDoneOutlined, SwapOutlined, DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons';
import LineItemTable from '../components/Tables/LineItemTable';
import Modals from '../components/Shared/Modal';
import handWritten from '../assets/images/handWritten.png';

import '../styles/reports.css';
import { useAuth } from '../context/AuthContext';

const { Option } = Select;
const { Text } = Typography;
const { Search } = Input;

const debounce = (fn: Function, delay: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { pagination, setTotal, setPage, setPageSize } = usePagination();
  const { page, pageSize, sort_by, sort_order } = pagination;
  const [panelData, setPanelData] = useState<any[]>([]);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  // const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  // const [s3Url, setS3Url] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sapProcess, setSapProcess] = useState<Record<string, boolean>>({});
  const [isEditingPo, setIsEditingPo] = useState<{ [key: string]: boolean }>({});
  const [updatedPoNumber, setUpdatedPoNumber] = useState<{ [key: string]: string }>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<any>({});
  const [totalElem, setTotalElement] = useState<string[]>([]);
  const [retrigger, setRetrigger] = useState<Record<string, boolean>>({});
  const { user } = useAuth();
  const [isEditingCompanyGst, setIsEditingCompanyGst] = useState<{ [key: string]: boolean }>({});
  const [updatedCompanyGstNumber, setUpdatedCompanyGstNumber] = useState<{ [key: string]: string }>({});
  const [isEditingVendorGst, setIsEditingVendorGst] = useState<{ [key: string]: boolean }>({});
  const [updatedVendorGstNumber, setUpdatedVendorGstNumber] = useState<{ [key: string]: string }>({});
  const [selectedSortField, setSelectedSortField] = useState<string>("default");
  const [selectedSortOrder, setSelectedSortOrder] = useState<string>('desc');

  const allowedAdmins = ['admin@invocr.com', 'admin@digiaim.ai'];

  const localUserString = localStorage.getItem('user');
  const localUser = localUserString ? JSON.parse(localUserString) : null;

  const showRetrigger = Boolean(
    localUser?.roles?.includes('Admin') && 
    localUser?.email && 
    allowedAdmins.includes(localUser.email)
  );

  const [paginationConfig, setPaginationConfig] = useState({
    current: page,
    pageSize: pageSize,
    total: 0,
    showTotal: (total: number, range: [number, number]) => `Showing ${range[0]}-${range[1]} of ${total} entries`,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50"],
    locale: { items_per_page: "" },
    onChange: (page: number, pageSize: number) => {
      setPage(page);
      setPageSize(pageSize);
      setPaginationConfig((prev: any) => ({
        ...prev,
        current: page,
        pageSize: pageSize,
      }));
    },
    onShowSizeChange: (current: number, size: number) => {
      setPage(current);
      setPageSize(size);
      setPaginationConfig((prev: any) => ({
        ...prev,
        current: current,
        pageSize: size,
      }));
    },
  });
  const openFilterModal = () => {
    setIsFilterModalOpen(true);
  };
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
    setPaginationConfig((prev: any) => ({
      ...prev,
      current: 1,
      pageSize: pageSize,
    }));
  };

  // Function to handle Success/Dispute button clicks
  const handleQuickFilter = (type: string) => {
    // 1. Reset pagination to page 1
    setPage(1);
    setPaginationConfig((prev: any) => ({
      ...prev,
      current: 1,
    }));

    // 2. Update the filter options
    // We check if the clicked type is already active. If yes, we clear it (toggle behavior).
    // If no, we set it.
    const isAlreadyActive = selectedFilterOptions?.statusPrefix === type;

    const newFilterData = {
      ...selectedFilterOptions, // Keep existing filters (like dates if needed)
      statusList: [], // Clear specific status list to avoid conflicts
      statusPrefix: isAlreadyActive ? "" : type // Set "Success", "Dispute", or empty
    };

    setSelectedFilterOptions(newFilterData);
    
    // Note: You don't need to call fetchPanelData() manually here 
    // because 'selectedFilterOptions' is in your useEffect dependency array.
  };

  const closeFilterModal = (data: any) => {
    setPage(1);
    setPaginationConfig((prev: any) => ({
      ...prev,
      current: 1,
      pageSize: pageSize,
    }));
    setIsFilterModalOpen(false);
    setSelectedFilterOptions(data ? data : {});
  };

  useEffect(() => {
    const handleResize = debounce(() => { }, 200);
    resizeObserverRef.current = new ResizeObserver(handleResize);
    const element = document.querySelector('.resize-observed-element');
    if (element) {
      resizeObserverRef.current.observe(element);
    }
    return () => {
      if (resizeObserverRef.current && element) {
        resizeObserverRef.current.unobserve(element);
      }
    };
    // eslint-disable-next-line
  }, [activeKeys]);

  const fetchPanelData = async () => {
    setLoading(true);
    try {
      let FData = localStorage.getItem('FilterData')
      let filterData = FData ? JSON.parse(FData) : null
      let body = Object.keys(selectedFilterOptions).length > 0 ? selectedFilterOptions : (filterData ? filterData : {})

      const response = await axios.post('/get-invoices', body, {
        params: {
          pageSize: pageSize,
          pageNo: page - 1,
          sortByField: (selectedSortField!="default" ? selectedSortField : sort_by) || sort_by,
          sortDirection: selectedSortOrder || 'desc',
          search: searchTerm.length > 1 ? searchTerm : ''
        },
      });

      if (response.data?.error === null) {
        setPanelData(response?.data?.data?.content);
        setTotal(response.data?.data?.totalElements);
        setTotalElement(response.data?.data?.totalElements)
        setPaginationConfig((prev) => ({
          ...prev,
          total: response.data?.data?.totalElements || 0,
        }));
      } else {
        console.error(response.data?.error);
      }
    } catch (error) {
      console.error('Failed to fetch panel data:', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchPanelData();
    // eslint-disable-next-line
  }, [page, pageSize, selectedSortField, selectedSortOrder, sort_by, sort_order, searchTerm, selectedFilterOptions]);

  const handleCollapseChange = (key: string | string[]) => {
    setActiveKeys(Array.isArray(key) ? key : [key]);
  };

  const handleSendClick = async (e: any, panel: any) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Confirm Send to SAP',
      content: 'Are you sure to send the invoice to SAP?',
      okText: 'Yes',
      onOk: async () => {
        try {
          setSapProcess((prevState) => ({
            ...prevState,
            [panel?.id]: true,
          }));
          const response = await axios.put(`/send-single-invoice-to-sap`, {}, {
            params: {
              id: panel?.id
            },
          });
          if (response.data?.error === null) {
            message.success("Sent to SAP");
            fetchPanelData();
          }
        } catch (error) {
          console.error('Failed to make API call:', error);
        } finally {
          setSapProcess((prevState) => ({
            ...prevState,
            [panel?.id]: false,
          }));
        }
      },
      onCancel() { }
    });
  };
  const handleRetrigger = async (e: any, panel: any) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Confirm OCR Retrigger',
      content: 'Are you sure to retrigger the OCR?',
      okText: 'Yes',
      onOk: async () => {
        try {
          setRetrigger((prevState) => ({
            ...prevState,
            [panel?.id]: true,
          }));
          const response = await axios.put(`/re-trigger-ocr`, {}, {
            params: {
              invoiceId: panel?.id
            },
          });
          if (response.data?.error === null) {
            message.success("Retriggered Successfully");
            fetchPanelData();
          }
        } catch (error) {
          console.error('Failed to make API call:', error);
        } finally {
          setRetrigger((prevState) => ({
            ...prevState,
            [panel?.id]: false,
          }));
        }
      },
      onCancel() { }
    });
  };

  const showPDFModel = (e: React.MouseEvent, panel: any) => {
    e.stopPropagation();
    const url = panel?.s3Url;
    window.open(url, '_blank');

    // if (url) {
    //   setS3Url(url);
    //   setIsModalOpen(true);
    // } else {
    //   message.error("No PDF Found", 4);
    // }
  };

  // const closeModal = () => {
  //   setIsModalOpen(false);
  //   setS3Url(null);
  // };

  // const sendToSap = async () => {
  //   try {
  //     const response = await axios.put(`/send-all-invoice-to-sap`);
  //     if (response.data?.error === null) {
  //       console.log('API call was successful:', response.data);
  //     } else {
  //       console.error('API error:', response.data?.error);
  //     }
  //   } catch (error) {
  //     console.error('Failed to make API call:', error);
  //   }
  // };

  const handlePoNumberChange = (e: React.ChangeEvent<HTMLInputElement>, panelId: string) => {
    e.stopPropagation();
    setUpdatedPoNumber({ ...updatedPoNumber, [panelId]: e.target.value });
  };

  const handleSavePoNumber = async (e: React.MouseEvent, panel: any) => {
    e.stopPropagation();
    const updatedPo = updatedPoNumber[panel.id];
    try {
      const payload = {
        ...panel,
        poNumber: updatedPo,
      };
      const response = await axios.post('/update-po-number', payload);
      if (response.data?.error === null) {
        message.success('PO number updated successfully');
        setIsEditingPo({ ...isEditingPo, [panel.id]: false });
        fetchPanelData(); // Refresh data after successful update
      } else {
        message.error('Failed to update PO number');
      }
    } catch (error) {
      console.error('Failed to update PO number:', error);
    }
  };

  const handleCancelEdit = (e: React.MouseEvent, panelId: string) => {
    e.stopPropagation();
    setIsEditingPo({ ...isEditingPo, [panelId]: false });
  };

  const handlePOEdit = (e: React.MouseEvent, panelId: string, poNumber: string) => {
    e.stopPropagation();
    setIsEditingPo({ ...isEditingPo, [panelId]: true });
    setUpdatedPoNumber({ ...updatedPoNumber, [panelId]: poNumber });
  };

  const SingleInvoiceEdit = (e: any, panelData: any) => {
    navigate('/invoice-edit', { state: panelData })
  }

  const viewInvoice = (e: any, panelData: any) => {
    const queryString = new URLSearchParams(panelData).toString();
    window.open(`/invoice-view?${queryString}`, '_blank');
  }

  const exportData = async () => {
    try {
      setLoading(true);
      let FData = localStorage.getItem('FilterData')
      let filterData = FData ? JSON.parse(FData) : null
      let body = Object.keys(selectedFilterOptions).length > 0 ? selectedFilterOptions : (filterData ? filterData : {})

      const response = await axios.post('/export-invoices', body, {
        params: {
          pageSize: pageSize,
          pageNo: page - 1,
          sortByField: sort_by,
          sortDirection: 'desc',
          search: searchTerm.length > 1 ? searchTerm : ''
        },
        responseType: 'blob'
      });

      // Generate current date and time in Indian format (DD-MM-YYYY_HH-MM-SS)
      const now = new Date();
      const indianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })); // Convert to Indian time zone
      const day = indianTime.getDate().toString().padStart(2, '0');
      const month = (indianTime.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-based
      const year = indianTime.getFullYear();
      const hours = indianTime.getHours().toString().padStart(2, '0');
      const minutes = indianTime.getMinutes().toString().padStart(2, '0');
      const seconds = indianTime.getSeconds().toString().padStart(2, '0');
      const formattedDate = `${day}-${month}-${year}_${hours}-${minutes}-${seconds}`;

      const fileName = `Invoices_Reports_${formattedDate}.xlsx`;

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Export successful!');
    } catch (error) {
      console.error('Export failed:', error);
      message.error('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e: any, panelData: any) => {
    Modal.confirm({
      title: 'Confirm Delete Duplicate Invoice',
      content: 'Are you sure to Delete Duplicate Invoice?',
      okText: 'Yes',
      onOk: async () => {
        try {
          const response = await axios.delete(`/delete-duplicate-invoice?invoiceId=${panelData.id}`, {});
          if (response.data?.error === null) {
            message.success(response.data?.message);
            fetchPanelData();
          } else {
            console.error(response.data?.error);
          }
        } catch (error) {
          console.error('Failed to delete duplicate invoice');
        }
      },
      onCancel() { }
    
    });
  };

  const exportDeletedData = async () => {
    try {
      setLoading(true);
      let FData = localStorage.getItem('FilterData')
      let filterData = FData ? JSON.parse(FData) : null
      let body = Object.keys(selectedFilterOptions).length > 0 ? selectedFilterOptions : (filterData ? filterData : {})

      const response = await axios.post('/export-duplicate-delete-invoices', body, {
        params: {
          pageSize: pageSize,
          pageNo: page - 1,
          sortByField: sort_by,
          sortDirection: 'desc',
          search: searchTerm.length > 1 ? searchTerm : ''
        },
        responseType: 'blob'
      });

      // Generate current date and time in Indian format (DD-MM-YYYY_HH-MM-SS)
      const now = new Date();
      const indianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })); // Convert to Indian time zone
      const day = indianTime.getDate().toString().padStart(2, '0');
      const month = (indianTime.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-based
      const year = indianTime.getFullYear();
      const hours = indianTime.getHours().toString().padStart(2, '0');
      const minutes = indianTime.getMinutes().toString().padStart(2, '0');
      const seconds = indianTime.getSeconds().toString().padStart(2, '0');
      const formattedDate = `${day}-${month}-${year}_${hours}-${minutes}-${seconds}`;

      const fileName = `Deleted_Invoices_Reports_${formattedDate}.xlsx`;

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Export successful!');
    } catch (error) {
      console.error('Export failed:', error);
      message.error('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  //company gst details updation....
  const handleCompanyGstEdit = (e: React.MouseEvent, panelId: string, companyGst: string) => {
    e.stopPropagation();
    setIsEditingCompanyGst({ ...isEditingCompanyGst, [panelId]: true });
    setUpdatedCompanyGstNumber({ ...updatedCompanyGstNumber, [panelId]: companyGst });
  };

  const handleCompanyGstNumberChange = (e: React.ChangeEvent<HTMLInputElement>, panelId: string) => {
    e.stopPropagation();
    setUpdatedCompanyGstNumber({ ...updatedCompanyGstNumber, [panelId]: e.target.value });
  };

  const handleSaveCompanyGstNumber = async (e: React.MouseEvent, panel: any) => {
    e.stopPropagation();
    const updatedCompanyGst = updatedCompanyGstNumber[panel.id];
    try {
      const payload = {
        ...panel,
        brigadeGST: updatedCompanyGst,
      };
      const response = await axios.post('/update-company-gst', payload);
      if (response.data?.error === null) {
        message.success(response.data?.message);
        setIsEditingCompanyGst({ ...isEditingCompanyGst, [panel.id]: false });
        fetchPanelData();
      } else {
        message.error('Failed to update Company Gst');
      }
    } catch (error) {
      console.error('Failed to update Company Gst:', error);
    }
  };

  const handleCancelCompanyGstEdit = (e: React.MouseEvent, panelId: string) => {
    e.stopPropagation();
    setIsEditingCompanyGst({ ...isEditingCompanyGst, [panelId]: false });
  };

  //Vendor gst details updation....
  const handleVendorGstEdit = (e: React.MouseEvent, panelId: string, vendorGst: string) => {
    e.stopPropagation();
    setIsEditingVendorGst({ ...isEditingVendorGst, [panelId]: true });
    setUpdatedVendorGstNumber({ ...updatedVendorGstNumber, [panelId]: vendorGst });
  };

  const handleVendorGstNumberChange = (e: React.ChangeEvent<HTMLInputElement>, panelId: string) => {
    e.stopPropagation();
    setUpdatedVendorGstNumber({ ...updatedVendorGstNumber, [panelId]: e.target.value });
  };

  const handleSaveVendorGstNumber = async (e: React.MouseEvent, panel: any) => {
    e.stopPropagation();
    const updatedVendorGst = updatedVendorGstNumber[panel.id];
    try {
      const payload = {
        ...panel,
        vendorGST: updatedVendorGst,
      };
      const response = await axios.post('/update-vendor-gst', payload);
      if (response.data?.error === null) {
        message.success(response.data?.message);
        setIsEditingVendorGst({ ...isEditingVendorGst, [panel.id]: false });
        fetchPanelData();
      } else {
        message.error('Failed to update Vendor Gst');
      }
    } catch (error) {
      console.error('Failed to update Vendor Gst:', error);
    }
  };

  const handleCancelVendorGstEdit = (e: React.MouseEvent, panelId: string) => {
    e.stopPropagation();
    setIsEditingVendorGst({ ...isEditingVendorGst, [panelId]: false });
  };


  const renderPanelHeader = (panel: any) => (
    <Row>
      <Col span={0.5}>
        <Button
          type="text"
          icon={activeKeys.includes(panel.id.toString()) ? <MinusOutlined /> : <PlusOutlined />}
        />
      </Col>
      <Col span={19} className='align-center-div'>
        <Row className="header-list-row" style={{ width: '100%' }}>
          <Col span={2}>
            <p className='panel-head-txt'>Aerchain no</p>
            <p className='panel-text'>
              {panel?.code ? panel?.code : '-'}
            </p>
          </Col>
          <Col span={2}>
            <p className='panel-head-txt'>OCR Date</p>
            <p className='panel-text'>
              {panel?.creationDate != null
                ? panel?.creationDate
                : '-'}
            </p>
          </Col>
          <Col span={2}>
            <p className='panel-head-txt'>Invoice No</p>
            <p className='panel-text'>{panel?.invoiceReference ? panel?.invoiceReference : '-'}</p>
          </Col>
          <Col span={2}>
            <p className='panel-head-txt'>Invoice Date</p>
            <p className='panel-text'>{panel?.invoiceDate ? panel?.invoiceDate : '-'}</p>
          </Col>
          <Col span={4}>
            <p className='panel-head-txt'>Vendor Name</p>
            <p className='panel-text'>{panel?.vendorName ? panel?.vendorName : '-'}</p>
          </Col>
          <Col span={3}>
            <Row align="middle">
              <Col span={22}>
                <p className='panel-head-txt'>
                  PO Number
                  {!panel?.status?.toLowerCase().startsWith('success') && !panel?.status?.toLowerCase().startsWith('reject') && !isEditingPo[panel.id] ? (
                    <EditOutlined
                      onClick={(e) => handlePOEdit(e, panel.id, panel.poNumber)}
                      style={{ color: 'black', marginLeft: '5px', fontWeight: 'bolder' }}
                      title='Edit PO Number'
                    />
                  ) : null}
                </p>
                {!isEditingPo[panel.id] ? (
                  <p className='panel-text'>{panel?.poNumber ? panel?.poNumber : '-'}</p>
                ) : (
                  <div style={{ display: 'flex' }}>
                    <Input
                      value={updatedPoNumber[panel.id]}
                      onChange={(e) => handlePoNumberChange(e, panel.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: 'calc(100%)' }}
                    />
                    <CheckOutlined
                      onClick={(e) => handleSavePoNumber(e, panel)}
                      style={{ color: 'green', marginLeft: '7px', cursor: 'pointer' }}
                    />
                    <CloseOutlined
                      onClick={(e) => handleCancelEdit(e, panel.id)}
                      style={{ color: 'red', marginLeft: '7px', cursor: 'pointer' }}
                    />
                  </div>
                )}
              </Col>
            </Row>
          </Col>
          <Col span={2}>
            <p className='panel-head-txt'>Tax Amount</p>
            <p className='panel-text'>
              {panel?.taxAmount != null && !isNaN(panel?.taxAmount)
                ? panel?.taxAmount.toFixed(2)
                : '-'}
            </p>
          </Col>
          <Col span={2}>
            <p className='panel-head-txt'>Total Amount</p>
            <p className='panel-text'>
              {panel?.totalAmount != null && !isNaN(panel?.totalAmount)
                ? panel?.totalAmount.toFixed(2)
                : '-'}
            </p>
          </Col>

          <Col span={16}>
            <p className='panel-head-txt'>Status</p>
            <p
              className='panel-text'
              style={{
                whiteSpace: 'pre-line',
                color: panel?.status?.toLowerCase().startsWith('success') && panel?.status.toLowerCase().includes('cancelled')
                  ? 'orange'
                  : panel?.status?.toLowerCase().startsWith('success')
                    ? 'green'
                    : panel?.status === ''
                      ? 'blue'
                      : 'red'
              }}
            >
              {panel?.status?.toLowerCase().startsWith('success') ? panel?.status.replace('. ', '.\n') : panel?.status ? panel.status : "Send to SAP"}
            </p>
          </Col>

          {/* company gst updation */}
          {user?.roles?.includes('Admin') && !panel?.status?.toLowerCase().startsWith('success') && !panel?.status?.toLowerCase().startsWith('reject') && (panel?.status?.toLowerCase().startsWith('dispute - company gst') || panel?.status?.toLowerCase().startsWith('dispute - vendor gst')) ? (
            <>
              <Col span={3}>
                <Row align="middle">              
                  <Col span={22}>                
                    <p className='panel-head-txt'>
                      Company Gst
                      { !isEditingCompanyGst[panel.id] ? (           
                        <EditOutlined
                          onClick={(e) => handleCompanyGstEdit(e, panel.id, panel.brigadeGST)}
                          style={{ color: 'black', marginLeft: '5px', fontWeight: 'bolder' }}
                          title='Edit Company Gst'
                        />
                      ) : null }
                    </p>
                    
                    {!isEditingCompanyGst[panel.id] ? (
                      <p className='panel-text'>{panel?.brigadeGST ? panel?.brigadeGST : '-'}</p>
                    ) : (
                      <div style={{ display: 'flex' }}>
                        <Input
                          value={updatedCompanyGstNumber[panel.id]}
                          onChange={(e) => handleCompanyGstNumberChange(e, panel.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: 'calc(100%)' }}
                        />
                        <CheckOutlined
                          onClick={(e) => handleSaveCompanyGstNumber(e, panel)}
                          style={{ color: 'green', marginLeft: '7px', cursor: 'pointer' }}
                        />
                        <CloseOutlined
                          onClick={(e) => handleCancelCompanyGstEdit(e, panel.id)}
                          style={{ color: 'red', marginLeft: '7px', cursor: 'pointer' }}
                        />
                      </div>
                    )}
                  </Col>              
                </Row>
              </Col>

              {/* vendor gst updation */}
              <Col span={3}>
                <Row align="middle">              
                  <Col span={22}>                
                    <p className='panel-head-txt'>
                      Vendor Gst
                      { !isEditingVendorGst[panel.id] ? (           
                        <EditOutlined
                          onClick={(e) => handleVendorGstEdit(e, panel.id, panel.vendorGST)}
                          style={{ color: 'black', marginLeft: '5px', fontWeight: 'bolder' }}
                          title='Edit Vendor Gst'
                        />
                      ) : null }
                    </p>
                    
                    {!isEditingVendorGst[panel.id] ? (
                      <p className='panel-text'>{panel?.vendorGST ? panel?.vendorGST : '-'}</p>
                    ) : (
                      <div style={{ display: 'flex' }}>
                        <Input
                          value={updatedVendorGstNumber[panel.id]}
                          onChange={(e) => handleVendorGstNumberChange(e, panel.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: 'calc(100%)' }}
                        />
                        <CheckOutlined
                          onClick={(e) => handleSaveVendorGstNumber(e, panel)}
                          style={{ color: 'green', marginLeft: '7px', cursor: 'pointer' }}
                        />
                        <CloseOutlined
                          onClick={(e) => handleCancelVendorGstEdit(e, panel.id)}
                          style={{ color: 'red', marginLeft: '7px', cursor: 'pointer' }}
                        />
                      </div>
                    )}
                  </Col>              
                </Row>
              </Col>
            </>            
          ) : null}
        </Row>
      </Col>
      <Col span={4} className='align-center-div'>
        <Row>
          {  panel?.handWrittenInvoiceFlag &&
            <Col style={{display:'flex', alignItems: 'center', cursor: 'default'}}>
              <img src={handWritten} title="Hand Written Invoice" style={{height:'20px'}}/>
            </Col>
          }
          {  panel?.editable &&
            <Col style={{display:'flex', alignItems: 'center', cursor: 'default'}}>
              <Button
                type="text"
                icon={<ThunderboltOutlined />}
                title={'EB Bill Invoices'}
              />
            </Col>
          }
          <Col>
          { panel?.duplicateInvoiceDeleteAccess &&
            <Button
              type="link"
              icon={<DeleteOutlined />}
              onClick={(e) => handleDelete(e, panel)}
              title={'Delete Duplicate Invoice'}
            />
          }
          </Col>
          <Col>
            {(!panel?.status?.toLowerCase().startsWith('success') && panel?.status?.toLowerCase() !== 'duplicate invoice processed' && panel?.status?.toLowerCase() !== 'dispute - duplicate invoice processed') && !panel?.status?.toLowerCase().startsWith('reject') &&
              <Button
                type="text"
                icon={<EditOutlined />}
                title={'Edit Invoice'}
                onClick={(e) => SingleInvoiceEdit(e, panel)}
              />
            }
          </Col>
          <Col>
            {(panel?.status?.toLowerCase().startsWith('success') || panel?.status?.toLowerCase().startsWith('reject')) &&
              <Button
                type="text"
                icon={<FileDoneOutlined />}
                title={'View'}
                onClick={(e) => viewInvoice(e, panel)}
              />
            }
          </Col>
          <Col>
            {sapProcess[panel?.id] ? (
              <span className="spin-overlap">
                <Spin size='default' />
              </span>
            ) : ((!panel?.status?.toLowerCase().startsWith('success') && panel?.status?.toLowerCase() !== 'duplicate invoice processed' && panel?.status?.toLowerCase() !== 'dispute - duplicate invoice processed') && !panel?.status?.toLowerCase().startsWith('reject') &&
              <Button
                type="text"
                icon={<SendOutlined />}
                title={'Send to SAP'}
                onClick={(e) => handleSendClick(e, panel)}
              />)}
          </Col>
          {showRetrigger &&
            <Col>
              {retrigger[panel?.id] ? (
                <span className="spin-overlap">
                  <Spin size='default' />
                </span>
              ) : ((!panel?.status?.toLowerCase().startsWith('success') && panel?.status?.toLowerCase() !== 'duplicate invoice processed' && panel?.status?.toLowerCase() !== 'dispute - duplicate invoice processed') && !panel?.status?.toLowerCase().startsWith('reject') &&
                <Button
                  type="text"
                  icon={<SwapOutlined />}
                  title={'Retrigger Ocr'}
                  onClick={(e) => handleRetrigger(e, panel)}
                />)}
            </Col>
          }
          <Col>
            <Button
              type="text"
              icon={<EyeOutlined />}
              title={'Preview Invoice'}
              onClick={(e) => showPDFModel(e, panel)}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );


  return (
    <>
      <Card className="reportsCard" title={
        <div>
          <Search
            className='searchInput'
            type="text"
            onSearch={handleSearch}
            id='searchField'
            allowClear={true}
            placeholder="Search..."
          />
        </div>
      } bordered={false} extra={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          
          <Select
            placeholder="Sort By"
            style={{ width: 140, marginRight: '8px' }}
            onChange={(value) => {
              setSelectedSortField(value);
              if (value === "default") {
                setSelectedSortOrder('desc');
              }
            }}
            allowClear 
            value={selectedSortField}
          >
            {/* You can add an explicit Default option if allowClear isn't enough */}
            <Option value="default">Default</Option>
            <Option value="creationDate">OCR Date</Option>
            <Option value="invoiceDate">Invoice Date</Option>
          </Select>

          <Select
            defaultValue="desc"
            style={{ width: 120, marginRight: '16px' }}
            onChange={(value) => setSelectedSortOrder(value)}
            value={selectedSortOrder}
          >
            <Option value="asc">Ascending</Option>
            <Option value="desc">Descending</Option>
          </Select>

          <Button 
            type={selectedFilterOptions?.statusPrefix === 'Success' ? "primary" : "default"} 
            style={{ marginRight: '8px' }} 
            onClick={() => handleQuickFilter('Success')}
          >
            Success
          </Button>
          
          <Button 
            type={selectedFilterOptions?.statusPrefix === 'Dispute' ? "primary" : "default"} 
            style={{ marginRight: '8px' }} 
            onClick={() => handleQuickFilter('Dispute')}
          >
            Dispute
          </Button>

          {
            user?.roles?.includes('Admin') && 
            <Button type="default" style={{ marginRight: '8px' }} onClick={exportDeletedData} title='Export Duplicate Deleted Invoices'>
              Del.Export
            </Button>
          }

          <Button type="default" style={{ marginRight: '8px' }} onClick={exportData}>
            Export
          </Button>

          <Button
            onClick={openFilterModal}
          > Filter
          </Button>
        </div>
      }>
        <div>

        </div>
        {loading ? (
          <div className="spin-container">
            <Spin size='default' /><span style={{ marginLeft: '7px' }}>Loading...</span>
          </div>
        ) : panelData.length > 0 ? (
          <div className="resize-observed-element">
            <Text className='totalcount' type='secondary'>Total no records: {totalElem}</Text>
            <Collapse
              activeKey={activeKeys}
              onChange={handleCollapseChange}
              bordered={false}
              accordion={true}
              className='mt-2'
            >
              {panelData.map((panel) => (
                <Collapse.Panel
                  header={renderPanelHeader(panel)}
                  key={panel?.id.toString()}
                >
                  <div>
                    <LineItemTable panelID={panel?.id} rejectionRemark={panel?.rejectionRemark} isEditOption={panel?.status?.toLowerCase().startsWith('success')} status={panel?.status} />
                  </div>
                </Collapse.Panel>
              ))}
            </Collapse>
            <div className="pagination-container">
              <Pagination {...paginationConfig} />
            </div>
          </div>
        ) : (
          <Empty />
        )}
      </Card >
      {/* <Modals isOpen={isModalOpen} onClose={closeModal} pdfUrl={s3Url} from={'pdf'} /> */}
      <Modals isOpen={isFilterModalOpen} onClose={closeFilterModal} pdfUrl={null} from={'filter'} />
    </>
  );
};

export default ReportsPage;
